<template>
  <div v-if="editing" class="inline-edit" :class="'inline-edit--' + type" @click.stop>
    <textarea
      v-if="type === 'textarea'"
      ref="inputRef"
      :value="value"
      @input="$emit('input', $event.target.value)"
      class="inline-edit__textarea"
      :rows="rows"
      @keydown.ctrl.s.prevent="$emit('save')"
      @keydown.escape="$emit('cancel')"
    ></textarea>
    <input
      v-else
      ref="inputRef"
      :value="value"
      @input="$emit('input', $event.target.value)"
      class="inline-edit__input"
      @keydown.ctrl.s.prevent="$emit('save')"
      @keydown.escape="$emit('cancel')"
    />
    <div class="inline-edit__actions">
      <v-icon :small="type === 'textarea'" :x-small="type !== 'textarea'" color="green" @click="$emit('save')" title="Save (Ctrl+S)">mdi-check</v-icon>
      <v-icon :small="type === 'textarea'" :x-small="type !== 'textarea'" color="grey" @click="$emit('cancel')" title="Cancel (Esc)">mdi-close</v-icon>
    </div>
  </div>
  <div v-else-if="saving" class="inline-edit__saving">
    <v-progress-circular indeterminate size="14" width="2" color="primary" class="mr-2"></v-progress-circular>
    Saving...
  </div>
  <span
    v-else
    :class="{ 'inline-edit__editable': editable }"
    @click.stop="$emit('start')"
  ><slot /></span>
</template>

<script>
import { ref, watch, nextTick } from 'vue';

export default {
  name: 'GridInlineEdit',
  props: {
    editing: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    value: { type: String, default: '' },
    editable: { type: Boolean, default: false },
    type: { type: String, default: 'input' },
    rows: { type: Number, default: 6 },
  },
  setup(props) {
    const inputRef = ref(null);

    watch(() => props.editing, (val) => {
      if (val) {
        nextTick(() => {
          if (inputRef.value) inputRef.value.focus();
        });
      }
    });

    return { inputRef };
  },
};
</script>

<style scoped>
/* Editable span (idle state) */
.inline-edit__editable {
  cursor: pointer;
  border-radius: 3px;
  padding: 1px 4px;
}

.inline-edit__editable:hover {
  background: rgba(0, 0, 0, 0.04);
  outline: 1px dashed #ccc;
}

/* Input mode */
.inline-edit--input {
  display: flex;
  align-items: center;
  gap: 4px;
}

.inline-edit--input .inline-edit__actions {
  display: flex;
  gap: 2px;
}

.inline-edit__input {
  flex: 1;
  padding: 2px 6px;
  border: 1px solid #1976d2;
  border-radius: 3px;
  font-size: 13px;
  font-family: inherit;
  outline: none;
  min-width: 40px;
}

/* Textarea mode */
.inline-edit--textarea {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.inline-edit--textarea .inline-edit__editable {
  padding: 2px 4px;
  margin: -2px -4px;
}

.inline-edit__textarea {
  width: 100%;
  font-size: 14px;
  font-family: 'DM Sans';
  font-weight: 400;
  letter-spacing: 0;
  color: #000000;
  line-height: 140%;
  padding: 4px 6px;
  border: 1px solid #3498db;
  border-radius: 4px;
  resize: vertical;
  outline: none;
  box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.15);
}

.inline-edit--textarea .inline-edit__actions {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
}

/* Saving state */
.inline-edit__saving {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #666;
  padding: 2px 0;
}

/* Icon styling */
.inline-edit__actions >>> .v-icon {
  cursor: pointer;
  opacity: 0.7;
}

.inline-edit__actions >>> .v-icon:hover {
  opacity: 1;
}

.inline-edit--textarea .inline-edit__actions >>> .v-icon {
  padding: 2px;
  border-radius: 3px;
}

.inline-edit--textarea .inline-edit__actions >>> .v-icon:hover {
  background-color: rgba(0, 0, 0, 0.06);
}
</style>
